import path from 'node:path';
import { getLanguageKeyForFilePath } from './language-registry.js';

const TS_JS_EXTENSIONS = ['', '.ts', '.tsx', '.js', '.jsx', '.mjs', '.cjs', '/index.ts', '/index.tsx', '/index.js', '/index.jsx'];
const PYTHON_EXTENSIONS = ['.py', '/__init__.py'];
const C_CPP_EXTENSIONS = ['', '.h', '.hpp', '.hh', '.hxx', '.c', '.cpp', '.cc', '.cxx'];

function normalizeWorkspacePath(filePath: string): string {
  return filePath.replace(/\\/g, '/').replace(/^\/+/, '');
}

function isRelativeModuleSpecifier(specifier: string): boolean {
  return specifier.startsWith('./') || specifier.startsWith('../') || specifier.startsWith('/');
}

function firstExistingPath(candidates: string[], workspaceFiles: ReadonlySet<string>): string | null {
  for (const candidate of candidates) {
    const normalized = normalizeWorkspacePath(candidate);
    if (workspaceFiles.has(normalized)) return normalized;
  }
  return null;
}

function withSuffixes(basePath: string, suffixes: readonly string[]): string[] {
  return suffixes.map((suffix) => basePath + suffix);
}

/** Candidates for TS/JS imports, including ESM `.js` specifiers that refer to `.ts` sources. */
function tsJsImportCandidates(joined: string): string[] {
  const candidates = withSuffixes(joined, TS_JS_EXTENSIONS);

  const extensionRewrites: ReadonlyArray<[string, readonly string[]]> = [
    ['.js', ['.ts', '.tsx', '.js', '.jsx']],
    ['.jsx', ['.tsx', '.ts', '.jsx', '.js']],
    ['.mjs', ['.mts', '.ts', '.mjs', '.js']],
    ['.cjs', ['.cts', '.ts', '.cjs', '.js']],
  ];
  for (const [fromExt, toExts] of extensionRewrites) {
    if (!joined.endsWith(fromExt)) continue;
    const stem = joined.slice(0, -fromExt.length);
    for (const toExt of toExts) {
      candidates.unshift(stem + toExt);
    }
    for (const indexSuffix of ['/index.ts', '/index.tsx', '/index.js', '/index.jsx']) {
      candidates.unshift(stem + indexSuffix);
    }
  }

  return candidates;
}

/**
 * Resolve a relative TS/JS module specifier to a workspace-relative file path using the indexed file list.
 */
export function resolveModuleImportPath(
  importerFilePath: string,
  moduleSpecifier: string,
  workspaceFiles: ReadonlySet<string>,
): string | null {
  const spec = moduleSpecifier.trim();
  if (!spec || !isRelativeModuleSpecifier(spec)) return null;

  const importerDir = path.posix.dirname(normalizeWorkspacePath(importerFilePath));
  const joined = normalizeWorkspacePath(path.posix.normalize(path.posix.join(importerDir, spec)));

  return firstExistingPath(tsJsImportCandidates(joined), workspaceFiles);
}

function resolvePythonModulePath(
  importerFilePath: string,
  moduleSpecifier: string,
  workspaceFiles: ReadonlySet<string>,
): string | null {
  const moduleName = moduleSpecifier.trim().replace(/\s+/g, '');
  if (!moduleName) return null;

  const importerDir = path.posix.dirname(normalizeWorkspacePath(importerFilePath));

  if (moduleName.startsWith('.')) {
    const dotMatch = /^(\.+)(.*)$/.exec(moduleName);
    const dotCount = dotMatch?.[1]?.length ?? 1;
    const rest = (dotMatch?.[2] ?? '').replace(/^\./, '');
    let baseDir = importerDir;
    for (let i = 1; i < dotCount; i++) {
      baseDir = path.posix.dirname(baseDir);
    }
    const dottedPath = rest ? rest.split('.').join('/') : '';
    const relativeBase = dottedPath ? path.posix.join(baseDir, dottedPath) : baseDir;
    return firstExistingPath(
      [
        ...withSuffixes(relativeBase, PYTHON_EXTENSIONS),
        ...withSuffixes(path.posix.join(relativeBase, '__init__'), ['.py']),
      ],
      workspaceFiles,
    );
  }

  const dottedPath = moduleName.split('.').join('/');

  return firstExistingPath(
    [
      ...withSuffixes(dottedPath, PYTHON_EXTENSIONS),
      ...withSuffixes(path.posix.join(importerDir, dottedPath), PYTHON_EXTENSIONS),
    ],
    workspaceFiles,
  );
}

function resolveIncludePath(
  importerFilePath: string,
  includePath: string,
  workspaceFiles: ReadonlySet<string>,
): string | null {
  const spec = includePath.trim().replace(/^["']|["']$/g, '');
  if (!spec) return null;

  const importerDir = path.posix.dirname(normalizeWorkspacePath(importerFilePath));
  const joined = normalizeWorkspacePath(
    path.posix.normalize(path.posix.join(importerDir, spec.replace(/\\/g, '/'))),
  );

  if (workspaceFiles.has(joined)) return joined;
  return firstExistingPath(withSuffixes(joined, C_CPP_EXTENSIONS), workspaceFiles);
}

function resolveJavaImportPath(moduleSpecifier: string, workspaceFiles: ReadonlySet<string>): string | null {
  const spec = moduleSpecifier.trim();
  if (!spec) return null;

  const pathLike = `${spec.replace(/\./g, '/')}.java`;
  if (workspaceFiles.has(pathLike)) return pathLike;

  for (const file of workspaceFiles) {
    if (file === pathLike || file.endsWith(`/${pathLike}`)) return file;
  }
  return null;
}

function resolveGoImportPath(
  importerFilePath: string,
  moduleSpecifier: string,
  workspaceFiles: ReadonlySet<string>,
): string | null {
  const spec = moduleSpecifier.trim();
  if (!spec) return null;

  if (isRelativeModuleSpecifier(spec)) {
    const importerDir = path.posix.dirname(normalizeWorkspacePath(importerFilePath));
    const joined = normalizeWorkspacePath(path.posix.normalize(path.posix.join(importerDir, spec)));
    if (workspaceFiles.has(joined)) return joined;
    return firstExistingPath(withSuffixes(joined, ['.go']), workspaceFiles);
  }

  const leaf = spec.split('/').filter(Boolean).at(-1);
  if (!leaf) return null;
  for (const file of workspaceFiles) {
    if (file.endsWith(`/${leaf}.go`) || file === `${leaf}.go`) return file;
  }
  return null;
}

/** Resolve an import/include specifier to a workspace-relative source file path. */
export function resolveImportSourcePath(
  importerFilePath: string,
  moduleSpecifier: string,
  workspaceFiles: ReadonlySet<string>,
): string | null {
  const spec = moduleSpecifier.trim();
  if (!spec) return null;

  const tsResult = resolveModuleImportPath(importerFilePath, spec, workspaceFiles);
  if (tsResult) return tsResult;

  const languageKey = getLanguageKeyForFilePath(importerFilePath);
  switch (languageKey) {
    case 'python':
      return resolvePythonModulePath(importerFilePath, spec, workspaceFiles);
    case 'c':
    case 'cpp':
      return resolveIncludePath(importerFilePath, spec, workspaceFiles);
    case 'java':
      return resolveJavaImportPath(spec, workspaceFiles);
    case 'go':
      return resolveGoImportPath(importerFilePath, spec, workspaceFiles);
    default:
      return null;
  }
}

export function isModulePathSpecifier(name: string): boolean {
  return isRelativeModuleSpecifier(name.trim());
}
